import { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../lib/auth";

type CompanyInfo = {
  id: number;
  name: string | null;
  ceo: string | null;
  biz_no: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  mail_order_no: string | null;
  updated_at?: string | null;
};

function v(s?: string | null) {
  return s ?? "";
}

export default function CompanyInfoPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [origin, setOrigin] = useState<CompanyInfo | null>(null);
  const [form, setForm] = useState({
    name: "",
    ceo: "",
    biz_no: "",
    address: "",
    phone: "",
    email: "",
    mail_order_no: "",
  });

  const dirty = useMemo(() => {
    if (!origin) return false;
    return (
      form.name !== v(origin.name) ||
      form.ceo !== v(origin.ceo) ||
      form.biz_no !== v(origin.biz_no) ||
      form.address !== v(origin.address) ||
      form.phone !== v(origin.phone) ||
      form.email !== v(origin.email) ||
      form.mail_order_no !== v(origin.mail_order_no)
    );
  }, [form, origin]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await authFetch("/api/admin/company-info");
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.message || "불러오기 실패");

      const row: CompanyInfo = data.row;
      setOrigin(row);
      setForm({
        name: v(row.name),
        ceo: v(row.ceo),
        biz_no: v(row.biz_no),
        address: v(row.address),
        phone: v(row.phone),
        email: v(row.email),
        mail_order_no: v(row.mail_order_no),
      });
    } catch (e: any) {
      setErr(e?.message || "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function reset() {
    if (!origin) return;
    setForm({
      name: v(origin.name),
      ceo: v(origin.ceo),
      biz_no: v(origin.biz_no),
      address: v(origin.address),
      phone: v(origin.phone),
      email: v(origin.email),
      mail_order_no: v(origin.mail_order_no),
    });
  }

  async function save() {
    if (!dirty) return;

    setSaving(true);
    setErr(null);
    try {
      const r = await authFetch("/api/admin/company-info", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim() || null,
          ceo: form.ceo.trim() || null,
          biz_no: form.biz_no.trim() || null,
          address: form.address.trim() || null,
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          mail_order_no: form.mail_order_no.trim() || null,
        }),
      });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.message || "저장 실패");

      const row: CompanyInfo = data.row;
      setOrigin(row);
      setForm({
        name: v(row.name),
        ceo: v(row.ceo),
        biz_no: v(row.biz_no),
        address: v(row.address),
        phone: v(row.phone),
        email: v(row.email),
        mail_order_no: v(row.mail_order_no),
      });
    } catch (e: any) {
      setErr(e?.message || "저장 실패");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="w-full">
      {/* 제목 */}
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">회사 정보</h1>
      </div>

      {/* 에러 */}
      {err && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* 로딩 */}
      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          불러오는 중...
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-6 py-6">
            {/* 첫번째 이미지 느낌: 2열, 라벨 위, 큰 입력 */}
            <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
              <Field
                label="상호명"
                value={form.name}
                onChange={(s) => setForm((p) => ({ ...p, name: s }))}
              />
              <Field
                label="대표"
                value={form.ceo}
                onChange={(s) => setForm((p) => ({ ...p, ceo: s }))}
              />

              <Field
                label="사업자등록번호"
                value={form.biz_no}
                onChange={(s) => setForm((p) => ({ ...p, biz_no: s }))}
              />
              <Field
                label="주소"
                value={form.address}
                onChange={(s) => setForm((p) => ({ ...p, address: s }))}
              />

              <Field
                label="전화번호"
                value={form.phone}
                onChange={(s) => setForm((p) => ({ ...p, phone: s }))}
              />
              <Field
                label="이메일"
                value={form.email}
                onChange={(s) => setForm((p) => ({ ...p, email: s }))}
              />

              <div className="md:col-span-2">
                <Field
                  label="통신판매업신고번호"
                  value={form.mail_order_no}
                  onChange={(s) => setForm((p) => ({ ...p, mail_order_no: s }))}
                />
              </div>
            </div>
          </div>

          {/* 하단 버튼: 첫 번째 이미지처럼 우측 정렬 */}
          <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
            <button
              type="button"
              onClick={reset}
              disabled={!dirty || saving}
              className={`h-10 rounded-md border px-6 text-sm font-medium ${
                !dirty || saving
                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              취소
            </button>

            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className={`h-10 rounded-md px-8 text-sm font-semibold text-white ${
                !dirty || saving
                  ? "cursor-not-allowed bg-slate-300"
                  : "bg-slate-900 hover:bg-slate-800"
              }`}
            >
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <div className="mb-2 text-sm font-semibold text-slate-700">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-md border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none focus:border-slate-500"
      />
    </label>
  );
}
